import React, { useState } from 'react';

import { Pencil, Plus, Search } from 'lucide-react';

import { PARTNER_TYPE_LABELS, useListPartnersQuery } from '../../api/partners';
import { useAuth } from '../../providers/AppAuthProvider';
import { SkeletonTable } from '../common';

import PartnerLogo from './PartnerLogo';
import {
	btn,
	card,
	input,
	select,
	selectChevron,
	SettingsPageHeader,
	td,
	th,
} from './settingsV9';

import type { PartnerType } from '../../api/partners';

interface PartnersListProps {
	onOpen: (partnerId: string) => void;
	onViewTemplates: () => void;
}

const PartnersList: React.FC<PartnersListProps> = ({
	onOpen,
	onViewTemplates,
}) => {
	const { isAdmin } = useAuth();
	const [type, setType] = useState<PartnerType | ''>('');
	const [search, setSearch] = useState('');
	const { data: partners = [], isLoading } = useListPartnersQuery();

	const query = search.trim().toLowerCase();
	const rows = partners.filter(
		(p) =>
			(!type || p.partner_type === type) &&
			(!query ||
				[p.name, p.contact_name, p.contact_email, p.statement_mailbox]
					.join(' ')
					.toLowerCase()
					.includes(query)),
	);

	return (
		<div className="space-y-4">
			<SettingsPageHeader
				title="Partners"
				subtitle="Each partner has its own brand, pricing templates and Schedule A."
				actions={
					isAdmin && (
						<button className={btn.primary} onClick={() => onOpen('new')}>
							<Plus className="h-4 w-4" />
							Add partner
						</button>
					)
				}
			/>
			<div className={`${card} overflow-hidden`}>
				<div className="flex items-center justify-between gap-4 px-6 pt-6">
					<div className="relative w-[360px] max-w-full">
						<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
						<input
							className={`${input} bg-gray-50 border-gray-200 pl-9`}
							placeholder="Search partners, contacts or mailboxes…"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
						/>
					</div>
					<div className="flex items-center gap-3">
						<select
							aria-label="Partner type"
							value={type}
							onChange={(e) => setType(e.target.value as PartnerType | '')}
							className={`${select} w-[180px]`}
							style={selectChevron}
						>
							<option value="">All types</option>
							{Object.entries(PARTNER_TYPE_LABELS).map(([value, label]) => (
								<option key={value} value={value}>
									{label}
								</option>
							))}
						</select>
					</div>
				</div>

				<div className="mt-4 overflow-x-auto">
					{isLoading ? (
						<SkeletonTable rows={5} columns={5} />
					) : (
						<table className="w-full border-collapse">
							<thead>
								<tr>
									{[
										'Partner',
										'Contact',
										'Statement mailbox',
										'Templates',
										'Actions',
									].map((heading) => (
										<th key={heading} className={th}>
											{heading}
										</th>
									))}
								</tr>
							</thead>
							<tbody>
								{rows.map((partner) => (
									<tr
										key={partner.id}
										onClick={() => onOpen(partner.id)}
										className="cursor-pointer hover:bg-gray-50"
									>
										<td className={td}>
											<span className="flex items-center gap-2.5 min-w-0">
												<PartnerLogo partner={partner} />
												<span>
													<span className="font-medium text-primary-700">
														{partner.name}
													</span>
													<br />
													<span className="text-xs text-gray-500">
														{PARTNER_TYPE_LABELS[partner.partner_type]}
													</span>
												</span>
											</span>
										</td>
										<td className={`${td} whitespace-normal`}>
											<span className="text-gray-900">
												{partner.contact_name || '—'}
											</span>
											<br />
											<span className="text-xs text-gray-500">
												{partner.contact_email}
											</span>
										</td>
										<td className={`${td} whitespace-normal text-[13px]`}>
											{partner.statement_mailbox || '—'}
										</td>
										<td className={td}>
											<button
												className={btn.link}
												onClick={(e) => {
													e.stopPropagation();
													onViewTemplates();
												}}
											>
												{partner.template_count} templates
											</button>
										</td>
										<td className={td}>
											<button
												className={btn.secondarySm}
												aria-label={`Edit ${partner.name}`}
												onClick={(e) => {
													e.stopPropagation();
													onOpen(partner.id);
												}}
											>
												<Pencil className="h-4 w-4" />
												{isAdmin ? 'Edit' : 'View'}
											</button>
										</td>
									</tr>
								))}
								{rows.length === 0 && (
									<tr>
										<td colSpan={5} className={`${td} text-gray-500`}>
											{partners.length === 0
												? 'No partners yet. Add one to group statements and templates.'
												: 'No partner matches. Clear the search or pick another type.'}
										</td>
									</tr>
								)}
							</tbody>
						</table>
					)}
				</div>
				<div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
					<span className="text-xs text-gray-500">
						Showing {rows.length} of {partners.length} partners
					</span>
				</div>
			</div>
		</div>
	);
};

export default PartnersList;
